import {
  RESOURCE_TYPES,
  type ResourceTypeValue,
} from "./resource-types";

export const LATEST_CONTENT_LIMIT = 3;

/** Enough recent items per type so category filters still find matches in the visual editor. */
export const LATEST_CONTENT_PREFETCH_LIMIT = 50;

export const LATEST_CONTENT_RESOURCE_TYPES = RESOURCE_TYPES.filter(
  (type) => type.value !== "video"
);

const ALLOWED_TYPES = new Set(
  LATEST_CONTENT_RESOURCE_TYPES.map((type) => type.value)
);

export type LatestContentSource = {
  resourceType: ResourceTypeValue;
  categories: string[];
};

export const DEFAULT_LATEST_CONTENT_SOURCES: LatestContentSource[] = [
  { resourceType: "blog", categories: [] },
  { resourceType: "news", categories: [] },
  { resourceType: "advisory", categories: [] },
];

export function parseLatestContentSource(
  source: unknown
): LatestContentSource | null {
  const resourceType = (source as { resourceType?: ResourceTypeValue })
    ?.resourceType;
  if (!resourceType || !ALLOWED_TYPES.has(resourceType)) {
    return null;
  }

  const categories = Array.isArray((source as { categories?: unknown })?.categories)
    ? (source as { categories: unknown[] }).categories.filter(
        (category: unknown): category is string =>
          typeof category === "string" && category.length > 0
      )
    : [];

  return { resourceType, categories };
}

export function resolveLatestContentSources(
  sources: unknown
): LatestContentSource[] {
  if (!Array.isArray(sources) || sources.length === 0) {
    return DEFAULT_LATEST_CONTENT_SOURCES;
  }

  const resolved = sources.flatMap((source): LatestContentSource[] => {
    const parsed = parseLatestContentSource(source);
    return parsed ? [parsed] : [];
  });

  return resolved.length > 0 ? resolved : DEFAULT_LATEST_CONTENT_SOURCES;
}

export function getLatestContentTabMeta(source: LatestContentSource) {
  const config = LATEST_CONTENT_RESOURCE_TYPES.find(
    (type) => type.value === source.resourceType
  );

  const label = config?.labels.plural || source.resourceType;
  const indexUrl = config?.indexUrl || source.resourceType;

  return {
    label,
    allLabel: `See all ${label.toLowerCase()}`,
    allLink: `/${indexUrl}`,
  };
}

export function resourceMatchesCategories(
  resource: { categories?: Array<{ category?: { categorySlug?: string | null } | null } | null> | null },
  categories: string[]
) {
  if (categories.length === 0) {
    return true;
  }

  return (resource.categories || []).some((entry) =>
    categories.includes(entry?.category?.categorySlug || "")
  );
}

export function latestContentSourceKey(source: LatestContentSource) {
  return `${source.resourceType}:${[...source.categories].sort().join(",")}`;
}

export function sourcesMatch(
  a: LatestContentSource,
  b: LatestContentSource
) {
  return latestContentSourceKey(a) === latestContentSourceKey(b);
}

export function takeLatestMatchingResources(
  nodes: any[] | undefined,
  categories: string[] = []
) {
  return (nodes || [])
    .filter((resource) => resource?.published === true)
    .filter((resource) => resourceMatchesCategories(resource, categories))
    .slice(0, LATEST_CONTENT_LIMIT);
}
